/// <reference types="google.maps" />
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

/**
 * Reemplaza a google.maps.drawing.DrawingManager (eliminado en Maps JS API v3.65)
 * usando Terra Draw. Conserva la MISMA firma pública que la versión anterior
 * (`constructor(map)` + `enableDrawing(onComplete)`), por lo que PolygonsManager,
 * main.js y editar-zonas.html no requieren cambios: enableDrawing sigue devolviendo
 * un google.maps.Polygon nativo a través del callback onComplete.
 */
class DrawManager
{
    public version: string = 'DrawManager.TerraDraw.v202607061629';

    private draw: TerraDraw | null = null;
    private ready: boolean = false;
    private pendingEnable: boolean = false;
    private onPolygonCompleteParams: ((polygon: google.maps.Polygon) => void) | null = null;

    constructor(private map: google.maps.Map)
    {
        // El adaptador de Google Maps necesita la proyección del mapa (OverlayView),
        // disponible de forma asíncrona. Al crearse de forma perezosa (al pulsar
        // "dibujar"), normalmente ya está lista → inicializamos de inmediato.
        // Si todavía no lo está, esperamos una única vez el evento projection_changed.
        if (this.map.getProjection())
            this.initTerraDraw();
        else
            google.maps.event.addListenerOnce(this.map, 'projection_changed', () => this.initTerraDraw());
    }

    private initTerraDraw(): void
    {
        this.draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({
                lib: google.maps,
                map: this.map
            }),
            modes: [
                new TerraDrawPolygonMode({
                    styles: {
                        fillColor: '#ffff00',
                        fillOpacity: 0.5,
                        outlineColor: '#ffff00',
                        outlineWidth: 2
                    }
                })
            ]
        });

        // Registrar listeners ANTES de start() para no perder un 'ready' inmediato.
        this.draw.on('ready', () =>
        {
            this.ready = true;
            if (this.pendingEnable)
            {
                this.pendingEnable = false;
                this.draw?.setMode('polygon');
            }
        });

        this.draw.on('finish', (id, context) =>
        {
            // 'finish' también se emite al editar/arrastrar vértices; solo nos interesa el dibujo.
            if (context.action !== 'draw') return;

            const feature = this.draw?.getSnapshotFeature(id);

            // Volver a modo estático y quitar la feature de Terra Draw: el polígono real
            // pasa a ser un google.maps.Polygon nativo gestionado por PolygonsManager.
            this.draw?.setMode('static');
            this.draw?.removeFeatures([id]);

            if (feature && feature.geometry.type === 'Polygon')
            {
                const polygon = this.geoJsonToGooglePolygon(feature.geometry.coordinates as number[][][]);
                if (this.onPolygonCompleteParams)
                    this.onPolygonCompleteParams(polygon);
            }
        });

        this.draw.start();
    }

    public enableDrawing(onComplete?: (polygon: google.maps.Polygon) => void)
    {
        if (onComplete) this.onPolygonCompleteParams = onComplete;

        // Si Terra Draw aún no terminó su init asíncrono, encolamos: el handler 'ready'
        // activará el modo polígono en cuanto esté disponible.
        if (this.draw && this.ready)
            this.draw.setMode('polygon');
        else
            this.pendingEnable = true;
    }

    private geoJsonToGooglePolygon(coordinates: number[][][]): google.maps.Polygon
    {
        // GeoJSON Polygon.coordinates = [anilloExterior, ...huecos]; cada vértice es [lng, lat]
        // (orden inverso al de Google {lat, lng}). El anillo viene cerrado; Google no lo exige.
        const paths = coordinates.map(ring =>
            ring.map((coord) => ({ lat: coord[1], lng: coord[0] }))
        );

        return new google.maps.Polygon({
            paths,
            map: this.map, // Adjuntar al mapa: AddPolygonAsync NO asigna map a un polígono ya provisto
                           // (solo lo hace al crearlo). El DrawingManager viejo devolvía el polígono ya en el mapa.
            editable: true,
            draggable: true,
            fillColor: '#ffff00',
            fillOpacity: 0.5,
            strokeWeight: 2
        });
    }
}

export { DrawManager };
